import "./AIScribe.init";

import {
  AudioInputDeviceSelect,
  isFinishedStep,
  isIdleStep,
  isPausedStep,
  isRecordingActiveStep,
  useScribe,
  useScribeReady,
  useStep,
  type SaveTranscriptItem,
  type UseScribeOptions,
} from "@elation/transcribe";
import { HeaderText } from "@src/components/general/HeaderText";
import {
  HorizontalFlexbox,
  VerticalFlexbox,
} from "@src/components/layout/Flexbox";
import { Button } from "@src/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@src/components/ui/popover";
import { Spinner } from "@src/components/ui/spinner";
import { useIsInElationEHR } from "@src/features/iframe";
import { cn } from "@src/lib/utils";
import {
  Check,
  FileText,
  Mic,
  Play,
  Settings,
  Sparkles,
  Square,
} from "lucide-react";
import { useMemo } from "react";
import { Transcript } from "./components/Transcript";

const DEFAULT_WEBSOCKET_URL = "ws://localhost:8080/ws/transcribe";

const stubSaveTranscriptItem: SaveTranscriptItem = async () => {
  // no-op default used when the parent does not provide a saver
};

export type AIScribeProps = UseScribeOptions & {
  isActiveEncounter?: boolean;
  encounterCompleted: boolean;
  encounterFinalizing: boolean;
  hasSoapnote?: boolean;
  onFinishVisit: () => Promise<void>;
  onStartVisit: () => Promise<string>;
  lastOffset: number;
  encounterId: number | null;
};

function resolveUseScribeOptions(props: AIScribeProps): UseScribeOptions {
  return {
    websocketUrl: props.websocketUrl ?? DEFAULT_WEBSOCKET_URL,
    getScribingSessionId: props.getScribingSessionId,
    saveTranscriptItem: props.saveTranscriptItem ?? stubSaveTranscriptItem,
    lastOffset: props.lastOffset,
  };
}

export function AIScribe(props: AIScribeProps) {
  const encounterCompleted = props.encounterCompleted ?? false;
  const hasSoapnote = props.hasSoapnote ?? false;
  const { onFinishVisit, onStartVisit } = props;

  const scribeOptions = useMemo(
    () => resolveUseScribeOptions(props),
    [props.websocketUrl, props.getScribingSessionId, props.saveTranscriptItem],
  );

  const isInElationEHR = useIsInElationEHR();

  const { startRecording, stopRecording } = useScribe(scribeOptions);

  const isScribeReady = useScribeReady();
  const [step] = useStep();

  const startDisabled = !isScribeReady || encounterCompleted;

  return (
    <HorizontalFlexbox alignItems="center" gap="xs" data-step={step}>
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="ghost" size="icon" aria-label="Transcript">
            <FileText />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          side="top"
          align="end"
          className="w-[28rem] p-3"
          aria-label="Transcript"
        >
          <Transcript encounterId={props.encounterId} />
        </PopoverContent>
      </Popover>

      {hasSoapnote ? (
        <Button
          type="button"
          size="sm"
          variant="secondary"
          className="gap-1.5 px-3"
          disabled
          aria-label="Visit completed"
        >
          <Check className="size-4" />
          <span className="hidden sm:inline">Visit Completed</span>
        </Button>
      ) : (
        <>
          {step === "finishing" ? <Spinner size="lg" /> : null}

          {isIdleStep(step) &&
          !isPausedStep(step) &&
          !isFinishedStep(step) &&
          !isInElationEHR ? (
            <Button
              type="button"
              size="sm"
              className="gap-1.5 px-3"
              disabled={startDisabled}
              aria-label={
                step === "finished" ? "Continue visit" : "Start visit"
              }
              onClick={async () => {
                const authToken = await onStartVisit();
                startRecording(authToken);
              }}
            >
              <Mic className="size-4" />
              <span className="hidden sm:inline">
                {step === "finished" ? "Continue Visit" : "Start Visit"}
              </span>
            </Button>
          ) : null}

          {isRecordingActiveStep(step) ? (
            <Button
              type="button"
              size="sm"
              variant="destructive"
              className={cn("gap-1.5 px-3 shadow-sm")}
              aria-label="Stop recording"
              onClick={() => {
                stopRecording();
              }}
            >
              <Square className="size-4 fill-current" />
              <span className="hidden sm:inline">Stop</span>
            </Button>
          ) : null}

          {isPausedStep(step) ? (
            <Button
              type="button"
              size="sm"
              className="gap-1.5 px-3"
              aria-label="Resume recording"
              onClick={async () => {
                const scribingSessionId = await props.getScribingSessionId();
                startRecording(scribingSessionId);
              }}
            >
              <Play className="size-4" />
              <span className="hidden sm:inline">Resume</span>
            </Button>
          ) : null}

          {isFinishedStep(step) && onFinishVisit ? (
            <Button
              type="button"
              size="sm"
              className="gap-1.5 bg-emerald-600 px-3 text-white hover:bg-emerald-700"
              aria-label="Finish visit"
              onClick={async () => {
                await onFinishVisit();
              }}
            >
              <Sparkles className="size-4" />
              <span className="hidden sm:inline">Finish Visit</span>
            </Button>
          ) : null}
        </>
      )}

      {!isInElationEHR && (
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="ghost" size="icon" aria-label="Scribe settings">
              <Settings />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            side="top"
            className="w-80"
            aria-labelledby="audio-settings-heading"
          >
            <VerticalFlexbox gap="sm">
              <HeaderText
                tag="h3"
                fontWeight={600}
                className="text-sm"
                id="audio-settings-heading"
              >
                Audio Settings
              </HeaderText>
              <AudioInputDeviceSelect />
            </VerticalFlexbox>
          </PopoverContent>
        </Popover>
      )}
    </HorizontalFlexbox>
  );
}
